import { useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import { toast } from "sonner";
import { Clock } from "lucide-react";
import Loading from "../../../cors/Loading";
import { CircleCheck } from 'lucide-react';


export default function Page() {
  const { token } = useAuth();
  const [systems, setSystems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);

  const fetchSystems = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`${BASE_URL}/api/payment-system`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      });

      const result = await response.json();
      setSystems(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchSystems();
  }, [token]);

  const confirmUpdate = (system) => {
    setSelectedSystem(system);
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedSystem(null);
  };

  const updateStatus = async () => {
    if (!selectedSystem) return;

    try {
      await fetch(`${BASE_URL}/api/payment-system/${selectedSystem.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      });

      fetchSystems();
      toast.success("Paiement reglé");
      setShowModal(false);
      setSelectedSystem(null);
    } catch (err) {
      toast.error("Update failed");
      setShowModal(false);
      setSelectedSystem(null);
    }
  };

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <>
      {/* Confirmation Modal */}
      {showModal && selectedSystem && (
        <div className="fixed inset-0 z-[99] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
              onClick={handleCancel}
            />
            
            {/* Modal */}
            <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
              <div className="flex items-center justify-center mb-4">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                  <svg
                    className="h-6 w-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              
              <div className="text-center">
                <h3 className="text-sm font-semibold leading-6 text-gray-900 mb-2">
                  Confirmer le paiement
                </h3>
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-4">
                    Êtes-vous sûr de vouloir marquer ce paiement comme réglé ?
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-xs text-gray-600 mb-1">Montant:</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {parseFloat(selectedSystem.total).toFixed(2)} MAD
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  className="inline-flex w-full cursor-pointer justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  onClick={handleCancel}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="inline-flex w-full cursor-pointer justify-center rounded-md border border-transparent bg-green-700 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-green-800"
                  onClick={updateStatus}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12">
        <div className="mb-10">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-2">
                Payment System
              </h1>
              <p className="text-sm text-gray-600">
                Les frais de system
              </p>
            </div>
            <button
              onClick={fetchSystems}
              className="flex cursor-pointer items-center gap-2 text-xs py-2 px-4 bg-[var(--color-primary-500)] text-white rounded-lg hover:bg-[var(--color-primary-400)] transition-colors duration-300"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Actualiser
            </button>
          </div>
        </div>
        
        {isLoading
          ? 
          <div className="w-[100%] flex justify-center pt-40">
            <Loading />
          </div>
          : 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1">
            {systems.map((system) => (
              <div
                key={system.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
              >
                {/* Header with ID and Status */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="bg-red-100 p-3 max-w-max rounded-md">
                      <Clock className="w-5 h-5 text-red-600" />
                    </div>
                  </div>

                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all duration-300
                      ${
                        system.status === 'true'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100 group-hover:bg-emerald-100'
                          : 'bg-rose-50 text-rose-700 border-rose-100 group-hover:bg-rose-100'
                      }`}
                  >
                    {system.status === 'true' ? (
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                        Réglé
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 bg-rose-500 rounded-full"></div>
                        Unpaid
                      </div>
                    )}
                  </span>
                </div>

                {/* Amount Section */}
                <div className="mb-2">
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-gray-800">
                    {new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(system.total)}
                    </p>
                    <span className="text-gray-500 font-medium text-xs">MAD</span>
                  </div>
                </div>

                {/* Action Button */}
                {system.status === 'false' && (
                  <div className="flex justify-end border-t border-gray-100 pt-2">
                    <button
                      onClick={() => confirmUpdate(system)}
                      className="cursor-pointer py-1.5 px-2 rounded-md bg-green-700 text-white font-semibold hover:shadow-lg hover:shadow-emerald-100 hover:bg-green-800"
                    >
                      <span className="flex items-center text-xs justify-center gap-1">
                        <CircleCheck className="w-3.5 h-3.5 text-white" />
                        Régler
                      </span>
                    </button>
                  </div>
                )}

                {/* Paid State Indicator */}
                {system.status === 'true' && (
                  <div className="w-full py-3 rounded-xl bg-gray-50 text-center">
                    <p className="text-sm font-medium text-gray-600 flex items-center justify-center gap-2">
                      <svg
                        className="w-4 h-4 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Payment Completed
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        }

        {!isLoading && systems.length === 0 && (
          <div className="text-center pt-32">
            <div className="w-32 h-32 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-12 h-12 text-[var(--color-primary-500)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xs text-gray-700 mb-2">
              Aucun paiement trouvé
            </h3>
          </div>
        )}
      </div>
    </>
  );
}